import { type AvailableCoupon } from '@hooks-api/types/dataModel/coupon'
import { Box } from '@pets-ui/Box/Box'
import { Chip } from '@pets-ui/Chip/Chip'
import { Gift } from '@pets-ui/Icons'
import { Image } from '@pets-ui/Image/Image'
import { Skeleton } from '@pets-ui/Skeleton/Skeleton'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import cartUiI18nInstance from '../i18n/i18nConfig'
import { CouponImageSystemDefault, type CouponImageSystemDefaultProps } from './CouponImageSystemDefault'
import { parseSystemDefaultMessage } from './parseSystemDefaultMessage'

const styles = stylex.create({
  chip: {
    backgroundColor: colors.red600,
    color: colors.white,
    position: 'absolute',
    left: 0,
    top: 0,
  },
})

export interface CouponImageProps {
  criterionGroups: AvailableCoupon['criterion_groups']
  imageUrl?: string
  variant?: CouponImageSystemDefaultProps['variant']
}

export const CouponImage = (props: CouponImageProps) => {
  const { criterionGroups, imageUrl, variant = 'default' } = props
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const { titlePrefix, titleSuffix, subTitle } = parseSystemDefaultMessage(criterionGroups)

  const titleSuffixText = t(`cartUi.couponImage.titleSuffix.${titleSuffix.message}`, { ...titleSuffix.value, defaultValue: titleSuffix.message })
  const subTitleText = t(`cartUi.couponImage.subTitle.${subTitle}`, { defaultValue: subTitle })

  const renderDefault = useCallback(() => {
    return (
      <CouponImageSystemDefault
        titlePrefix={titlePrefix}
        titleSuffix={titleSuffixText}
        subTitle={subTitleText}
        variant={variant}
      />
    )
  }, [subTitleText, titlePrefix, titleSuffixText, variant])

  if (!imageUrl) return renderDefault()

  return (
    <Box position="relative" borderRadius={8} overflow="hidden">
      <Chip variant="square" rootStyle={styles.chip} size="small" zIndex={1}>
        {titlePrefix}
        {titleSuffixText}
      </Chip>
      <Image
        width={64}
        height={64}
        src={imageUrl}
        loadingFallback={<Skeleton width={64} height={64} />}
        fallback={<Gift fontSize={64} />}
        alt="coupon image"
      />
    </Box>
  )
}
